import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type { OpsSupportRequestRow, UnitUsageUnit } from '@nadoutong/contracts';
import { parseUsageDetailsAdmission } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { usePricing } from '../api/pricing.ts';
import { useSupportRequests } from '../api/useSupportRequests.ts';
import {
  AdmissionInterceptionBadge,
  AdmissionInterceptionPanel,
} from '../components/AdmissionInterception.tsx';
import {
  formatLocalTimestamp,
  formatUsdMicrosAsCny,
  formatUsdMicrosOrCentsAsCnyMetered,
} from '../components/formatters.ts';
import { isRatingPending } from '../components/rating-status.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { parseTenantCostBreakdown } from '../lib/tenantCostBreakdown.ts';
import type {
  UnitBreakdownEntry,
  UnpricedReason,
  UnknownBreakdownEntry,
} from '../lib/tenantCostBreakdown.ts';
import {
  Button,
  Chip,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
} from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';

/**
 * `/support/requests` — per-tenant request-log viewer for the Support role
 * (Epic #109 / P0.4). Used for billing-dispute investigation: "which
 * requests did this account actually make, how much did each cost, and
 * which upstream served them?"
 *
 * Fan-out happens on ops-api: this page hits a single endpoint that
 * merges gateway `usage_logs` with platform `accounts` + `api_keys`.
 *
 * Filters:
 *   - `accountId` — required. Query is disabled until a value is
 *     entered so typing and mousing don't fire mis-scoped requests.
 *   - `from` / `to` — datetime-local inputs, converted to ISO-UTC on
 *     apply. Default window is the last 24h (resolved server-side when
 *     omitted). Server caps the window at 90 days.
 *   - `requestId` — optional exact-match lookup. When set, the time
 *     range is ignored and at most one row is returned.
 *
 * Tooling conventions:
 *   - Draft vs. applied state: typing doesn't issue a request; the
 *     operator must click Apply (matches Audit/BillingAnomalies).
 *   - Pagination: offset-based (Previous/Next). Filter changes reset
 *     offset to 0.
 *   - Status codes render as Dispatch Deck chips (2xx ok / 4xx warn /
 *     5xx bad).
 */

const PAGE_LIMIT = 50;

interface FilterState {
  accountId: string;
  from: string;
  to: string;
  requestId: string;
}

const EMPTY_FILTERS: FilterState = {
  accountId: '',
  from: '',
  to: '',
  requestId: '',
};

function formatLatency(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');
function formatTokens(n: number): string {
  return TOKEN_FORMATTER.format(n);
}

function truncateId(value: string, head = 10, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

/**
 * Convert a `<input type="datetime-local">` value to ISO-UTC so the
 * server sees a canonical Date.parse-able string. Returns empty string
 * when the input is empty or unparseable (caller skips the param).
 */
function localInputToIsoUtc(raw: string): string {
  if (raw.length === 0) return '';
  const parsed = Date.parse(raw);
  if (!Number.isFinite(parsed)) return '';
  return new Date(parsed).toISOString();
}

function statusChipTone(code: number): ChipTone {
  if (code >= 200 && code < 300) return 'ok';
  if (code >= 400 && code < 500) return 'warn';
  if (code >= 500) return 'bad';
  return 'neutral';
}

/** Human labels for the closed unit-kind set (#1650). */
const UNIT_LABEL_KEYS: Record<UnitUsageUnit, I18nKey> = {
  call: 'support.requests.unit.call',
  second: 'support.requests.unit.second',
  character: 'support.requests.unit.character',
  image: 'support.requests.unit.image',
};

/**
 * Labels for the zero-charge markers (`buildZeroBreakdown`, #1650 / #1937).
 *
 * `Record<UnpricedReason, …>` is exhaustive on purpose: a marker the parser
 * starts surfacing without a label here is a typecheck failure, not a blank
 * cell on a billing investigation.
 */
const UNPRICED_LABEL_KEYS: Record<UnpricedReason, I18nKey> = {
  unit_price_missing: 'support.requests.unit_unpriced.unit_price_missing',
  unit_not_delivered: 'support.requests.unit_unpriced.unit_not_delivered',
  variant_price_missing: 'support.requests.unit_unpriced.variant_price_missing',
  unit_surcharges_malformed: 'support.requests.unit_unpriced.unit_surcharges_malformed',
};

/**
 * Quantity cells show the stored value verbatim (Intl grouping only): a
 * negative or fractional quantity is a data anomaly the operator must SEE,
 * not have floored or hidden by a token-style formatter. The local
 * `formatTokens` above already has exactly that behavior; the alias exists
 * so quantity call sites read as counts, not tokens.
 */
const formatCount = formatTokens;

/**
 * Stored-money display for breakdown cells, currency-consistent with the
 * row-level cost column (CNY via the same ops display rate). Both stored
 * projections absent means the writer recorded no money at all — render
 * '—', never a confident ¥0.00 (H1, #1654 review).
 */
function formatStoredCny(
  usdMicros: number | null,
  usdCents: number | null,
  cnyPerUsd: number | null,
): string {
  if (usdMicros === null && usdCents === null) return '—';
  return formatUsdMicrosOrCentsAsCnyMetered(usdMicros, usdCents, cnyPerUsd, false);
}

/**
 * Detail cells for one unit-metered breakdown row (#1654). Every money
 * value is the stored micros/cents projection — never `quantity × rate`
 * recomputed here.
 */
function UnitBreakdownCells({
  rowId,
  index,
  entry,
  cnyPerUsd,
}: {
  readonly rowId: string;
  readonly index: number;
  readonly entry: UnitBreakdownEntry;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const idBase = `support-row-${rowId}-unit-${index}`;
  const unitLabel = entry.unit !== null ? t(UNIT_LABEL_KEYS[entry.unit]) : entry.rawUnit;
  const variantLabel =
    entry.variantKey.length > 0
      ? entry.variantKey
      : t('support.requests.detail.unit_variant.none');
  return (
    <>
      <div>
        <dt className="text-label text-paper-low">
          {t('support.requests.detail.unit_kind')}
        </dt>
        <dd className="mt-0.5 text-dense text-paper" data-testid={`${idBase}-kind`}>
          {unitLabel}
        </dd>
      </div>
      <div>
        <dt className="text-label text-paper-low">
          {t('support.requests.detail.unit_quantity')}
        </dt>
        <dd className="mt-0.5 font-mono text-dense text-paper" data-testid={`${idBase}-quantity`}>
          {formatCount(entry.quantity)}
        </dd>
      </div>
      <div>
        <dt className="text-label text-paper-low">
          {t('support.requests.detail.unit_rate')}
        </dt>
        <dd className="mt-0.5 font-mono text-dense text-paper" data-testid={`${idBase}-rate`}>
          {formatUsdMicrosAsCny(entry.rateUsdMicrosPerUnit, cnyPerUsd, false)}
        </dd>
      </div>
      <div>
        <dt className="text-label text-paper-low">
          {t('support.requests.detail.unit_variant')}
        </dt>
        <dd className="mt-0.5 font-mono text-dense text-paper" data-testid={`${idBase}-variant`}>
          {variantLabel}
        </dd>
      </div>
      <div>
        <dt className="text-label text-paper-low">
          {t('support.requests.detail.unit_amount')}
        </dt>
        <dd className="mt-0.5 font-mono text-dense text-paper" data-testid={`${idBase}-amount`}>
          {formatStoredCny(entry.usdMicros, entry.usdCents, cnyPerUsd)}
        </dd>
      </div>
    </>
  );
}

/**
 * Tolerant fallback for a breakdown dimension this build does not
 * recognize: generic label + raw dimension name + stored money, so the
 * charge stays visible instead of vanishing or blanking the page.
 */
function UnknownDimensionCell({
  rowId,
  index,
  entry,
  cnyPerUsd,
}: {
  readonly rowId: string;
  readonly index: number;
  readonly entry: UnknownBreakdownEntry;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const name =
    entry.dimension.length > 0
      ? entry.dimension
      : t('support.requests.detail.unknown_dimension.unnamed');
  return (
    <div data-testid={`support-row-${rowId}-unknown-dim-${index}`}>
      <dt className="text-label text-paper-low">
        {t('support.requests.detail.unknown_dimension')}
      </dt>
      <dd className="mt-0.5 font-mono text-dense text-paper">
        {name} · {formatStoredCny(entry.usdMicros, entry.usdCents, cnyPerUsd)}
      </dd>
    </div>
  );
}

/**
 * The only visible trace of a quarantined / zero-cost charge (#1654 M1): its
 * breakdown envelope carries `dimensions: []` plus a marker in
 * `unpriced_dimensions`.
 *
 * Since #1937 that includes the TOKEN lane's `variant_price_missing` — a
 * delivered request, on a bought upstream, billed nothing because its declared
 * price tier had no row. Before it surfaced here that row was indistinguishable
 * from an ordinary free request on the one surface an operator investigates
 * billing from. Other token-path markers still never reach here; the parser
 * drops them.
 */
function UnitUnpricedCell({
  rowId,
  reasons,
}: {
  readonly rowId: string;
  readonly reasons: readonly UnpricedReason[];
}): JSX.Element {
  const t = useT();
  return (
    <div data-testid={`support-row-${rowId}-unit-unpriced`}>
      <dt className="text-label text-paper-low">
        {t('support.requests.detail.unit_unpriced')}
      </dt>
      <dd className="mt-0.5 text-dense text-paper">
        {reasons.map((reason) => t(UNPRICED_LABEL_KEYS[reason])).join(' · ')}
      </dd>
    </div>
  );
}

interface RowProps {
  readonly row: OpsSupportRequestRow;
  readonly expanded: boolean;
  readonly onToggle: (id: string) => void;
  readonly cnyPerUsd: number | null;
}

function SupportRow({ row, expanded, onToggle, cnyPerUsd }: RowProps): JSX.Element {
  const t = useT();
  // Parsed only when the panel is open; a collapsed page never touches the blob.
  const breakdown = expanded
    ? parseTenantCostBreakdown(row.tenant_cost_breakdown_json)
    : null;
  /**
   * Parsed on every row, unlike the breakdown above, because the badge is on
   * the collapsed row: an interception an operator has to expand each row to
   * find is not findable. This route spreads the gateway entry rather than
   * projecting it, so the raw column arrives here and the shared contracts
   * reader picks the same five fields the ops request list projects.
   */
  const admission = parseUsageDetailsAdmission(row.usage_details_json);
  const keyLabel =
    row.api_key_name !== null && row.api_key_name.length > 0
      ? row.api_key_name
      : t('support.requests.row.api_key.unnamed');
  const keyPrefix = row.api_key_prefix ?? '';

  return (
    <>
      <Tr
        data-testid={`support-row-${row.id}`}
        className="cursor-pointer"
        onClick={() => onToggle(row.id)}
      >
        <Td
          title={formatLocalTimestamp(row.created_at, { seconds: true })}
          data-testid={`support-row-${row.id}-when`}
          mono
        >
          {formatLocalTimestamp(row.created_at, { seconds: true })}
        </Td>
        <Td
          mono
          emphasis
          title={row.request_id}
          data-testid={`support-row-${row.id}-request-id`}
        >
          {truncateId(row.request_id)}
        </Td>
        <Td data-testid={`support-row-${row.id}-model`}>
          <span className="font-medium text-paper">{row.model}</span>
          <span className="ml-2 text-micro text-paper-low">{row.vendor}</span>
        </Td>
        <Td data-testid={`support-row-${row.id}-status`}>
          <div className="flex flex-col items-start gap-1">
            <Chip tone={statusChipTone(row.status_code)}>{row.status_code}</Chip>
            {admission !== null ? (
              <AdmissionInterceptionBadge
                admission={admission}
                testId={`support-row-${row.id}-admission`}
              />
            ) : null}
          </div>
        </Td>
        <Td num data-testid={`support-row-${row.id}-latency`}>
          {formatLatency(row.latency_ms)}
        </Td>
        <Td num emphasis data-testid={`support-row-${row.id}-cost`}>
          {isRatingPending(row.rating_status) ? (
            <span className="text-paper-low">{t('support.requests.cost.pending')}</span>
          ) : (
            formatUsdMicrosOrCentsAsCnyMetered(row.cost_usd_micros, row.cost_usd_cents, cnyPerUsd, false)
          )}
        </Td>
        <Td data-testid={`support-row-${row.id}-api-key`}>
          <span className="font-medium text-paper">{keyLabel}</span>
          {keyPrefix.length > 0 ? (
            <span className="ml-2 font-mono text-micro text-paper-low">{keyPrefix}</span>
          ) : null}
        </Td>
        <Td data-testid={`support-row-${row.id}-upstream`}>
          {row.upstream_name.length > 0
            ? row.upstream_name
            : t('support.requests.row.upstream.deleted')}
        </Td>
        <Td className="text-right">
          {expanded ? (
            <ChevronUp aria-hidden="true" className="inline h-4 w-4 text-paper-low" />
          ) : (
            <ChevronDown aria-hidden="true" className="inline h-4 w-4 text-paper-low" />
          )}
        </Td>
      </Tr>
      {expanded ? (
        <Tr interactive={false} data-testid={`support-row-${row.id}-details`}>
          <Td colSpan={9}>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg bg-well p-3 md:grid-cols-4">
              <div>
                <dt className="text-label text-paper-low">
                  {t('support.requests.detail.request_id')}
                </dt>
                <dd className="mt-0.5 font-mono text-dense text-paper">{row.request_id}</dd>
              </div>
              <div>
                <dt className="text-label text-paper-low">
                  {t('support.requests.detail.upstream_id')}
                </dt>
                <dd className="mt-0.5 font-mono text-dense text-paper">{row.upstream_id}</dd>
              </div>
              <div>
                <dt className="text-label text-paper-low">
                  {t('support.requests.detail.protocol')}
                </dt>
                <dd className="mt-0.5 text-dense text-paper">{row.protocol}</dd>
              </div>
              <div>
                <dt className="text-label text-paper-low">
                  {t('support.requests.detail.created_at')}
                </dt>
                <dd className="mt-0.5 font-mono text-dense text-paper">
                  {formatLocalTimestamp(row.created_at, { seconds: true })}
                </dd>
              </div>
              <div>
                <dt className="text-label text-paper-low">
                  {t('support.requests.detail.input_tokens')}
                </dt>
                <dd className="mt-0.5 font-mono text-dense text-paper">
                  {formatTokens(row.input_tokens)}
                </dd>
              </div>
              <div>
                <dt className="text-label text-paper-low">
                  {t('support.requests.detail.output_tokens')}
                </dt>
                <dd className="mt-0.5 font-mono text-dense text-paper">
                  {formatTokens(row.output_tokens)}
                </dd>
              </div>
              <div>
                <dt className="text-label text-paper-low">
                  {t('support.requests.detail.cache_read')}
                </dt>
                <dd className="mt-0.5 font-mono text-dense text-paper">
                  {formatTokens(row.cache_read_tokens)}
                </dd>
              </div>
              <div>
                <dt className="text-label text-paper-low">
                  {t('support.requests.detail.cache_write')}
                </dt>
                <dd className="mt-0.5 font-mono text-dense text-paper">
                  {formatTokens(row.cache_write_tokens)}
                </dd>
              </div>
              {breakdown?.unitEntries.map((entry, index) => (
                <UnitBreakdownCells
                  key={`unit-${index}-${entry.rawUnit}-${entry.variantKey}`}
                  rowId={row.id}
                  index={index}
                  entry={entry}
                  cnyPerUsd={cnyPerUsd}
                />
              ))}
              {breakdown?.unknownEntries.map((entry, index) => (
                <UnknownDimensionCell
                  key={`unknown-${index}-${entry.dimension}`}
                  rowId={row.id}
                  index={index}
                  entry={entry}
                  cnyPerUsd={cnyPerUsd}
                />
              ))}
              {breakdown !== null && breakdown.unpricedReasons.length > 0 ? (
                <UnitUnpricedCell rowId={row.id} reasons={breakdown.unpricedReasons} />
              ) : null}
            </dl>
            {/*
              A titled sibling of the detail grid, not cells inside it. The
              grid is a flat list of billing facts; an interception is the
              reason this row HAS no billing facts, and it needs to say so
              under its own name. Keeping it outside the `<dl>` also keeps the
              legacy cell set exactly what it was for every non-intercepted
              row, which is the #1654 shape lock.
            */}
            {admission !== null ? (
              <AdmissionInterceptionPanel
                admission={admission}
                // Detail-scoped, not row-scoped: the badge above already owns
                // `support-row-<id>-admission`, and both are in the DOM at once
                // on an expanded row.
                idBase={`support-detail-${row.id}-admission`}
                className="mt-3 rounded-lg bg-well p-3"
              />
            ) : null}
          </Td>
        </Tr>
      ) : null}
    </>
  );
}

export function SupportRequests(): JSX.Element {
  const t = useT();
  const [searchParams] = useSearchParams();
  const initialAccountId = searchParams.get('accountId') ?? '';
  const initialFilters = useMemo<FilterState>(
    () => ({ ...EMPTY_FILTERS, accountId: initialAccountId }),
    [initialAccountId],
  );
  const [draft, setDraft] = useState<FilterState>(initialFilters);
  const [applied, setApplied] = useState<FilterState>(initialFilters);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const query = useSupportRequests({
    accountId: applied.accountId,
    from: applied.from.length > 0 ? applied.from : undefined,
    to: applied.to.length > 0 ? applied.to : undefined,
    requestId: applied.requestId.length > 0 ? applied.requestId : undefined,
    limit: PAGE_LIMIT,
    offset,
  });
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;

  const rows = useMemo(() => query.data?.rows ?? [], [query.data]);
  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? offset;
  const currentLimit = query.data?.limit ?? PAGE_LIMIT;
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + currentLimit < total;
  const account = query.data?.account;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const accountId = draft.accountId.trim();
    if (accountId.length === 0) return;
    setApplied({
      accountId,
      from: localInputToIsoUtc(draft.from),
      to: localInputToIsoUtc(draft.to),
      requestId: draft.requestId.trim(),
    });
    setOffset(0);
    setExpandedId(null);
  }

  function handleReset(): void {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setOffset(0);
    setExpandedId(null);
  }

  function handleToggle(id: string): void {
    setExpandedId((current) => (current === id ? null : id));
  }

  const hasApplied = applied.accountId.length > 0;

  return (
    <section data-testid="support-requests-page" className="flex flex-col gap-6">
      <PageHeader title={t('support.requests.title')} sub={t('support.requests.subtitle')} />

      <form
        onSubmit={handleSubmit}
        data-testid="support-requests-filters"
        className="flex flex-wrap items-end gap-3 rounded-lg border border-line-soft bg-card p-4"
      >
        <FormField
          label={t('support.requests.filter.account_id')}
          fieldId="support-filter-account-id"
          required
        >
          <Input
            id="support-filter-account-id"
            type="text"
            required
            mono
            value={draft.accountId}
            onChange={(e) => setDraft({ ...draft, accountId: e.currentTarget.value })}
            placeholder={t('support.requests.filter.account_id.placeholder')}
            data-testid="support-filter-account-id"
            className="w-64"
          />
        </FormField>
        <FormField label={t('support.requests.filter.from')} fieldId="support-filter-from">
          <Input
            id="support-filter-from"
            type="datetime-local"
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.currentTarget.value })}
            data-testid="support-filter-from"
            className="w-56"
          />
        </FormField>
        <FormField label={t('support.requests.filter.to')} fieldId="support-filter-to">
          <Input
            id="support-filter-to"
            type="datetime-local"
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.currentTarget.value })}
            data-testid="support-filter-to"
            className="w-56"
          />
        </FormField>
        <FormField
          label={t('support.requests.filter.request_id')}
          fieldId="support-filter-request-id"
        >
          <Input
            id="support-filter-request-id"
            type="text"
            mono
            value={draft.requestId}
            onChange={(e) => setDraft({ ...draft, requestId: e.currentTarget.value })}
            placeholder={t('support.requests.filter.request_id.placeholder')}
            data-testid="support-filter-request-id"
            className="w-64"
          />
        </FormField>
        <div className="flex h-9 items-center gap-2">
          <Button
            type="submit"
            variant="primary"
            disabled={draft.accountId.trim().length === 0}
            data-testid="support-filter-apply"
          >
            {t('support.requests.filter.apply')}
          </Button>
          <Button type="button" onClick={handleReset} data-testid="support-filter-reset">
            {t('support.requests.filter.reset')}
          </Button>
        </div>
      </form>

      {!hasApplied ? (
        <EmptyState
          testId="support-requests-cta"
          className="rounded-lg border border-line-soft bg-card"
          title={t('support.requests.cta.title')}
          hint={t('support.requests.cta.body')}
        />
      ) : null}

      {hasApplied && query.isError ? (
        <div
          role="alert"
          data-testid="support-requests-error"
          className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3"
        >
          <div className="flex flex-col gap-1">
            <span className="text-body font-semibold text-bad">
              {t('support.requests.error.title')}
            </span>
            <span className="text-dense text-bad-bright">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => void query.refetch()}
            data-testid="support-requests-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {hasApplied && query.isPending ? (
        <div
          data-testid="support-requests-loading"
          className="flex flex-col gap-2 rounded-lg border border-line-soft bg-card p-4"
        >
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12" />
          ))}
          <span className="sr-only">{t('support.requests.loading_aria')}</span>
        </div>
      ) : null}

      {hasApplied && query.isSuccess && account !== undefined ? (
        <aside
          data-testid="support-requests-account"
          className="flex flex-wrap items-center gap-6 rounded-lg border border-line-soft bg-card p-4"
        >
          <div className="flex flex-col gap-0.5">
            <span className="text-label text-paper-low">
              {t('support.requests.account.account_id')}
            </span>
            <span className="font-mono text-dense text-paper">{account.account_id}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-label text-paper-low">
              {t('support.requests.account.email')}
            </span>
            <span className="text-dense text-paper">{account.email}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-label text-paper-low">
              {t('support.requests.account.org')}
            </span>
            <span className="text-dense text-paper">
              {account.org_name !== null
                ? account.org_name
                : t('support.requests.account.org.none')}
            </span>
          </div>
        </aside>
      ) : null}

      {hasApplied && query.isSuccess && rows.length === 0 ? (
        <EmptyState
          testId="support-requests-empty"
          className="rounded-lg border border-line-soft bg-card"
          title={t('support.requests.empty.title')}
          hint={t('support.requests.empty.body')}
        />
      ) : null}

      {hasApplied && query.isSuccess && rows.length > 0 ? (
        <>
          <Table testId="support-requests-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('support.requests.column.when')}</Th>
                <Th>{t('support.requests.column.request_id')}</Th>
                <Th>{t('support.requests.column.model')}</Th>
                <Th>{t('support.requests.column.status')}</Th>
                <Th num>{t('support.requests.column.latency')}</Th>
                <Th num>{t('support.requests.column.cost')}</Th>
                <Th>{t('support.requests.column.api_key')}</Th>
                <Th>{t('support.requests.column.upstream')}</Th>
                <Th className="text-right" />
              </Tr>
            </THead>
            <TBody>
              {rows.map((row) => (
                <SupportRow
                  key={row.id}
                  row={row}
                  expanded={expandedId === row.id}
                  onToggle={handleToggle}
                  cnyPerUsd={cnyPerUsd}
                />
              ))}
            </TBody>
          </Table>
          <nav
            aria-label={t('support.requests.pagination.aria_label')}
            data-testid="support-requests-pagination"
            className="flex items-center justify-between gap-4 rounded-lg border border-line-soft bg-card px-4 py-3"
          >
            <span className="text-dense text-paper-low">
              {t('support.requests.pagination.range', {
                start: currentOffset + 1,
                end: Math.min(currentOffset + rows.length, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!hasPrev || query.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
                data-testid="support-requests-pagination-prev"
              >
                {t('common.previous')}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!hasNext || query.isFetching}
                onClick={() => setOffset(offset + PAGE_LIMIT)}
                data-testid="support-requests-pagination-next"
              >
                {t('common.next')}
              </Button>
            </div>
          </nav>
        </>
      ) : null}
    </section>
  );
}
